/**
 * The write transport shared by the pages: the CSRF token, a JSON mutation and the reading of a failed
 * answer. A failure's copy comes from the server only as non-empty `text/plain` or as a JSON object's
 * non-empty string `error`; anything else - an error page, a body that cannot be read - and a caught
 * exception fall back to the caller's copy, so no raw exception text ever reaches the screen.
 */
export function resolveCsrfToken(documentRef = globalThis.document) {
    const tokenElement = documentRef?.querySelector?.('meta[name="csrf-token"]');
    if (!(tokenElement instanceof HTMLMetaElement)) {
        return '';
    }

    return String(tokenElement.content ?? '').trim();
}

/** The message of a failed answer, or `fallback` when the server gave none it may show. */
export async function readErrorMessage(response, fallback) {
    const { message } = await readBody(response);

    return message === '' ? fallback : message;
}

/**
 * One JSON write. `outcome` tells the three answers apart: `committed` (a success status with a
 * `success: true` object), `rejected` (an explicit refusal - any other status) and `unknown` (no answer,
 * or a success status whose body is unusable: the write may have landed). `unknownErrorMessage` is the
 * copy for `unknown`, else `fallbackErrorMessage`.
 * @returns {Promise<{ok: boolean, outcome: 'committed'|'rejected'|'unknown', payload: unknown, errorMessage: string}>}
 */
export async function sendJsonMutation(fetchImpl, {
    url,
    csrfToken,
    body,
    fallbackErrorMessage,
    unknownErrorMessage = fallbackErrorMessage,
}) {
    let response;
    try {
        response = await fetchImpl(url, {
            method: 'POST',
            headers: {
                Accept: 'application/json',
                'Content-Type': 'application/json',
                'X-CSRF-Token': csrfToken,
            },
            credentials: 'same-origin',
            body: JSON.stringify(body),
        });
    } catch (_error) {
        return { ok: false, outcome: 'unknown', payload: null, errorMessage: unknownErrorMessage };
    }

    const { payload, message } = await readBody(response);
    if (response?.ok === true) {
        return isRecord(payload) && payload.success === true
            ? { ok: true, outcome: 'committed', payload, errorMessage: '' }
            : { ok: false, outcome: 'unknown', payload, errorMessage: unknownErrorMessage };
    }

    return {
        ok: false,
        outcome: 'rejected',
        payload,
        errorMessage: message === '' ? fallbackErrorMessage : message,
    };
}

/** Consumes the body once: the parsed JSON (else null) and the message the server may show (else ''). */
async function readBody(response) {
    let text;
    try {
        text = String(await response.text());
    } catch (_error) {
        return { payload: null, message: '' };
    }

    const type = String(response?.headers?.get?.('Content-Type') ?? '').trim().toLowerCase();
    if (/^text\/plain\s*(;|$)/.test(type)) {
        return { payload: null, message: text.trim() };
    }

    let payload = null;
    try {
        payload = JSON.parse(text);
    } catch (_error) {
        return { payload: null, message: '' };
    }

    const error = isRecord(payload) && typeof payload.error === 'string' ? payload.error.trim() : '';

    return { payload, message: error };
}

function isRecord(value) {
    return typeof value === 'object' && value !== null && !Array.isArray(value);
}
